import { useEffect, useRef, useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import { takePastedCropMeta } from './pastedCropMeta';
import type { ImageCardsCropMeta } from './useImageCardsSplit';

/**
 * A hero image block's crop metadata: taken from the paste that created the
 * block, or restored on load as `primed`, and saved against the block so
 * "Adjust crop" survives a reload.
 */
export function useHeroCropMeta({
  blockId,
  modelId,
  modelType,
  backendService,
  primed,
  onPrimedConsumed,
}: {
  blockId: string;
  modelId: string;
  modelType: string;
  backendService: BackendService;
  primed?: ImageCardsCropMeta;
  onPrimedConsumed?: () => void;
}) {
  const [meta, setMeta] = useState<ImageCardsCropMeta | null>(null);

  const persist = (next: ImageCardsCropMeta) => {
    if (!next.source || !backendService.saveImageCardCropSource) return;
    backendService
      .saveImageCardCropSource({
        modelType,
        modelId,
        contentBlockId: blockId,
        source: next.source,
        boxes: next.boxes,
      })
      .catch((err) => console.warn('Could not save the crop source for Adjust crop:', err));
  };

  // Latest callbacks, so the hand-off effects below run only when the block or the primed meta changes.
  const persistRef = useRef(persist);
  persistRef.current = persist;
  const consumedRef = useRef(onPrimedConsumed);
  consumedRef.current = onPrimedConsumed;

  useEffect(() => {
    const pasted = takePastedCropMeta(blockId);
    if (!pasted?.source) return;
    setMeta(pasted);
    persistRef.current(pasted);
  }, [blockId]);

  useEffect(() => {
    if (!primed?.source) return;
    setMeta(primed);
    consumedRef.current?.();
  }, [primed]);

  return {
    meta,
    /** Record new boxes after a re-crop, and save them. */
    update: (next: ImageCardsCropMeta) => {
      setMeta(next);
      persist(next);
    },
    /** The image was replaced by hand, so the kept screenshot no longer applies. */
    forget: () => setMeta(null),
  };
}
